import { infiniteQueryOptions, useInfiniteQuery } from "@tanstack/react-query";
import * as React from "react";

import { useAuth } from "@/providers";
import { useDomainServices } from "@/providers/DomainServices";

import { type DashboardService } from "../DashboardService";

import { dashboardQueryKeys } from "./dashboardQueryKeys";

/**
 * Infinite-query options for an org's saved-dashboard list, following the
 * server's `next_token` cursor. Extracted from the hook so node unit tests
 * can drive the cursor chain through a bare `InfiniteQueryObserver` (the
 * "unit" vitest project has no DOM to mount the hook in).
 */
export function dashboardsListQueryOptions(
  dashboardService: DashboardService,
  orgId: string | undefined,
) {
  return infiniteQueryOptions({
    queryKey: dashboardQueryKeys.list(orgId),
    queryFn: ({ pageParam, signal }) => {
      if (orgId === undefined) {
        return Promise.reject(new Error("CurrentOrganization is not set"));
      }

      return dashboardService.getDashboardsForOrg(orgId, {
        abortSignal: signal,
        pageToken: pageParam,
      });
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.next_token,
    // Consumers (the dashboard picker) want one flat list; pages are a
    // transport detail.
    select: (data) => data.pages.flatMap((page) => page.items),
    // Deleting a dashboard elsewhere (another tab/user) must not leave a
    // stale entry lingering in the picker. Same-session mutations already
    // invalidate this key, so this short cadence is the cross-session safety
    // net: poll every minute, and keep `staleTime` well under the interval so
    // focus/mount refetches also refresh a list that has gone stale. The
    // global default is a 5-minute `staleTime` with no polling, which is too
    // slow for a list whose items can vanish. Scoped to this query only.
    // `refetchInterval` polls only while the query has active observers and
    // (with `refetchIntervalInBackground` defaulting to false) pauses when the
    // tab is unfocused.
    refetchInterval: 60_000,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
}

/**
 * Every saved dashboard in the current org. `data` is a flat
 * `DashboardRecord[]` spanning ALL server pages: the picker shows one flat
 * list, so the hook eagerly drains the cursor chain instead of waiting for a
 * "load more" interaction (the `useDatasetSessions` pattern).
 */
export function useDashboards() {
  const { dashboardService } = useDomainServices();
  const { currentOrganization } = useAuth();

  const query = useInfiniteQuery({
    ...dashboardsListQueryOptions(
      dashboardService,
      currentOrganization?.org_id,
    ),
    enabled: currentOrganization !== null,
  });
  const { hasNextPage, isFetchingNextPage, isError, fetchNextPage } = query;

  // Stop on error: `hasNextPage` stays true after a failed page fetch, so
  // refiring would retry forever.
  React.useEffect(
    function loadAllPages() {
      if (hasNextPage && !isFetchingNextPage && !isError) {
        void fetchNextPage();
      }
    },
    [hasNextPage, isFetchingNextPage, isError, fetchNextPage],
  );

  return query;
}
